export enum AuthErrorType {
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  RATE_LIMITED = 'RATE_LIMITED',
  WEAK_PASSWORD = 'WEAK_PASSWORD',
  EMAIL_IN_USE = 'EMAIL_IN_USE',
  INVALID_EMAIL = 'INVALID_EMAIL',
  UNKNOWN = 'UNKNOWN'
}

interface AuthErrorResponse {
  message: string;
  status?: number;
}

export class AuthError extends Error {
  readonly status: number;
  readonly type: AuthErrorType;

  constructor(message: string, status: number = 500, type: AuthErrorType = AuthErrorType.UNKNOWN) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
    this.type = type;
  }

  static invalidCredentials(): AuthError {
    return new AuthError(
      'Invalid email or password',
      401,
      AuthErrorType.INVALID_CREDENTIALS
    );
  }

  static sessionExpired(): AuthError {
    return new AuthError(
      'Your session has expired. Please sign in again.',
      401,
      AuthErrorType.SESSION_EXPIRED
    );
  }

  static rateLimited(): AuthError {
    return new AuthError(
      'Too many attempts. Please try again later.',
      429,
      AuthErrorType.RATE_LIMITED
    );
  }

  static weakPassword(): AuthError {
    return new AuthError(
      'Password must be at least 8 characters long and contain uppercase, lowercase, numbers, and special characters.',
      400,
      AuthErrorType.WEAK_PASSWORD
    );
  }

  static emailInUse(): AuthError {
    return new AuthError(
      'This email is already registered.',
      409,
      AuthErrorType.EMAIL_IN_USE
    );
  }

  static invalidEmail(): AuthError {
    return new AuthError(
      'Please enter a valid email address.',
      400,
      AuthErrorType.INVALID_EMAIL
    );
  }

  static fromError(error: Error | AuthErrorResponse): AuthError {
    if (error instanceof AuthError) {
      return error;
    }

    return new AuthError(
      error.message || 'An unexpected error occurred',
      'status' in error ? error.status : 500,
      AuthErrorType.UNKNOWN
    );
  }
} 